import React from 'react';
import { FormattedMessage, IntlProvider, createIntl } from 'react-intl';
import PropTypes from 'prop-types';
import ar from './ar.json';
import en from './en.json';
import Grid from '@mui/material/Unstable_Grid2';
import Card from '@mui/material/Card';
import Fade from '@mui/material/Fade';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { LocaleContext } from '../../../contexts/language';
import { BrandContext } from '../../../contexts/branding';

const QuestionFormModal = React.lazy(() => import('../Forms/QuestionFormModal'));
const CallbackFormModal = React.lazy(() => import('../Forms/CallbackFormModal'));
const TestDriveFormModal = React.lazy(() => import('../Forms/TestDriveFormModal'));

import List from '@mui/material/List';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItem from '@mui/material/ListItem';
import CheckIcon from '@mui/icons-material/Check';

const languages = {
  ar, en
};

export const PureGetHelpPanel = ({
  helpImageElement,
  testDriveImageElement,
  allowRequestCallback,
  allowTestDrive,
  onSubmitTestDriveForm,
  onSubmitCallbackForm,
  onSubmitEnquiryForm,
  ...rest
}) => {
  const localeConfig = React.useContext(LocaleContext);
  const brandConfig = React.useContext(BrandContext);
  const intl = createIntl({
    locale: localeConfig?.lang || 'en', messages: languages[localeConfig?.lang] || languages.en
  });

  const [showCallbackForm, setShowCallbackForm] = React.useState(false);
  const [showQuestionForm, setShowQuestionForm] = React.useState(false);
  const [showTestDriveForm, setShowTestDriveForm] = React.useState(false);

  return (<>
    <IntlProvider locale={localeConfig?.lang} messages={languages[localeConfig?.lang] || languages.en}>
      <React.Suspense fallback={null}>
        <TestDriveFormModal
          {...rest}
          onSubmit={onSubmitTestDriveForm}
          onClose={() => setShowTestDriveForm(false)}
          open={showTestDriveForm}
        />

        <CallbackFormModal
          {...rest}
          onSubmit={onSubmitCallbackForm}
          onClose={() => setShowCallbackForm(false)}
          open={showCallbackForm}
        />

        <QuestionFormModal
          {...rest}
          onClose={() => setShowQuestionForm(false)}
          open={showQuestionForm}
          onSubmit={onSubmitEnquiryForm}
        />

      </React.Suspense>
      <Stack spacing={2}>
        {allowTestDrive ? (<Fade in>
          <Card          >
            <Grid container spacing={2}>
              <Grid xs={12} md={3} lg={4}>
                {testDriveImageElement}
              </Grid>
              <Grid xs={12} md={9} lg={8} sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Typography variant={'h5'}>
                    <FormattedMessage id={'pureBookTestDrivePanel.title'} defaultMessage={'Test Drive'} />
                  </Typography>
                  <Stack spacing={0}>
                    <Typography variant={'body1'} sx={{
                      fontSize: '14px'
                    }}>
                      <FormattedMessage
                        id={'pureBookTestDrivePanel.description'}
                        defaultMessage={'Book a free test drive in 30 seconds!'}
                      />
                    </Typography>
                    <List
                      sx={{ mt:0 }}
                      type="secondary"
                      dense
                    >
                      <ListItem>
                        <ListItemIcon>
                          <CheckIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary={intl.formatMessage({
                          id: 'pureBookTestDrivePanel.reason1', defaultMessage: 'Pick your preferred date and time'
                        })}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary={intl.formatMessage({
                          id: 'pureBookTestDrivePanel.reason2', defaultMessage: 'No Hassle'
                        })}
                        />
                      </ListItem>
                    </List>
                  </Stack>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Button
                      color="primary"
                      onClick={() => setShowTestDriveForm(true)}
                      variant={'contained'}
                    >
                      <FormattedMessage
                        id={'pureGetHelp.bookTestDrive'}
                        defaultMessage={'Book a Test Drive'}
                      />
                    </Button>

                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </Card>
        </Fade>) : null}
        <Fade in>
          <Card

          >
            <Grid container spacing={2}>
              <Grid xs={12} md={3} lg={4}>
                {helpImageElement}
              </Grid>
              <Grid xs={12} md={9} lg={8} sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Typography variant={'h5'}>
                    <FormattedMessage id={'pureGetHelp.gotAQuestion'} defaultMessage={'Got a question?'} />
                  </Typography>
                  <Typography variant={'body1'} sx={{
                    fontSize: '14px'
                  }}>
                    <FormattedMessage
                      id={'pureGetHelp.questionText'}
                      defaultMessage={'We\'re here to answer any questions you might have. Our offices are open 9-5 Monday to Friday and we usually get back to you within a couple of hours.'} />
                  </Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Button
                      color="primary"
                      onClick={() => setShowQuestionForm(true)}
                      variant={'contained'}
                    >
                      <FormattedMessage
                        id={'pureGetHelp.askAQuestion'}
                        defaultMessage={'Ask a Question'}
                      />
                    </Button>
                    {brandConfig.allowRequestCallback || allowRequestCallback ? (<Button
                      variant="contained"
                      color="secondary"
                      onClick={() => setShowCallbackForm(true)}
                    >
                      <FormattedMessage
                        id={'pureGetHelp.requestCallback'}
                        defaultMessage={'Request Callback'}
                      />
                    </Button>) : null}
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </Card>
        </Fade>
      </Stack>
    </IntlProvider>
  </>);
};

PureGetHelpPanel.propTypes = {
  helpImageElement: PropTypes.element.isRequired,
  testDriveImageElement: PropTypes.element.isRequired,
  allowRequestCallback: PropTypes.bool,
  onSubmitTestDriveForm: PropTypes.func.isRequired,
  onSubmitCallbackForm: PropTypes.func.isRequired,
  onSubmitEnquiryForm: PropTypes.func.isRequired
};

PureGetHelpPanel.defaultProps = {
  onSubmitTestDriveForm: (a) => {
    console.log({ form: a });
    return Promise.resolve(a);
  },
  onSubmitCallbackForm: (a) => {
    console.log({ form: a });
    return Promise.resolve(a);
  },
  onSubmitEnquiryForm: (a) => {
    console.log({ form: a });
    return Promise.resolve(a);
  }
};
